// src/pages/proyectos/components/SprintFormModal.tsx
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { addDays, format } from 'date-fns';
import { Dialog, DialogContent, Button, Input, Label, Textarea } from '@/components/ui';
import { useCrearSprint, useActualizarSprint } from '@/hooks/queries/useProyectos';
import type { ProyectoSprint, ProyectoSprintCreate, ProyectoSprintUpdate } from '@/types';

const schema = z.object({
  nombre: z.string().min(1, 'Nombre requerido').max(100, 'Máximo 100 caracteres'),
  numero: z.coerce.number().min(1, 'Número requerido'),
  fecha_inicio: z.string().min(1, 'Fecha inicio requerida'),
  fecha_fin: z.string().min(1, 'Fecha fin requerida'),
  objetivo: z.string().optional(),
  capacidad_horas: z.coerce.number().min(0).optional(),
});

type FormData = z.infer<typeof schema>;

interface Props {
  open: boolean;
  onClose: () => void;
  proyectoId: string;
  sprint?: ProyectoSprint;
  nextNumero?: number;
}

export function SprintFormModal({ open, onClose, proyectoId, sprint, nextNumero = 1 }: Props) {
  const crearMutation = useCrearSprint();
  const actualizarMutation = useActualizarSprint();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const fechaInicio = watch('fecha_inicio');

  useEffect(() => {
    if (sprint) {
      reset({
        nombre: sprint.nombre,
        numero: sprint.numero,
        fecha_inicio: sprint.fecha_inicio,
        fecha_fin: sprint.fecha_fin,
        objetivo: sprint.objetivo ?? '',
        capacidad_horas: sprint.capacidad_horas ?? undefined,
      });
    } else {
      const today = new Date();
      const endDate = addDays(today, 14);
      reset({
        nombre: `Sprint ${nextNumero}`,
        numero: nextNumero,
        fecha_inicio: format(today, 'yyyy-MM-dd'),
        fecha_fin: format(endDate, 'yyyy-MM-dd'),
        objetivo: '',
        capacidad_horas: undefined,
      });
    }
  }, [sprint, reset, nextNumero]);

  // Auto-update fecha_fin when fecha_inicio changes (only for new sprints)
  useEffect(() => {
    if (!sprint && fechaInicio) {
      const newEndDate = addDays(new Date(fechaInicio), 14);
      setValue('fecha_fin', format(newEndDate, 'yyyy-MM-dd'));
    }
  }, [fechaInicio, sprint, setValue]);

  const onSubmit = async (data: FormData) => {
    try {
      const payload = {
        ...data,
        objetivo: data.objetivo || undefined,
        capacidad_horas: data.capacidad_horas || undefined,
      };

      if (sprint) {
        await actualizarMutation.mutateAsync({
          sprintId: sprint.id,
          proyectoId,
          data: payload as ProyectoSprintUpdate,
        });
      } else {
        await crearMutation.mutateAsync({
          proyectoId,
          data: payload as ProyectoSprintCreate,
        });
      }
      onClose();
    } catch {
      // Error handling is done by react-query
    }
  };

  return (
    <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <h2 className="text-xl font-semibold mb-6">
          {sprint ? 'Editar Sprint' : 'Nuevo Sprint'}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <Label>Nombre *</Label>
              <Input
                {...register('nombre')}
                placeholder="Sprint 1"
                className="mt-1"
              />
              {errors.nombre && (
                <p className="text-sm text-red-500 mt-1">{errors.nombre.message}</p>
              )}
            </div>
            <div>
              <Label>Número *</Label>
              <Input
                type="number"
                {...register('numero')}
                min={1}
                className="mt-1"
              />
              {errors.numero && (
                <p className="text-sm text-red-500 mt-1">{errors.numero.message}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Fecha inicio *</Label>
              <Input
                type="date"
                {...register('fecha_inicio')}
                className="mt-1"
              />
              {errors.fecha_inicio && (
                <p className="text-sm text-red-500 mt-1">{errors.fecha_inicio.message}</p>
              )}
            </div>
            <div>
              <Label>Fecha fin *</Label>
              <Input
                type="date"
                {...register('fecha_fin')}
                className="mt-1"
              />
              {errors.fecha_fin && (
                <p className="text-sm text-red-500 mt-1">{errors.fecha_fin.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label>Objetivo del sprint</Label>
            <Textarea
              {...register('objetivo')}
              placeholder="¿Cuál es el objetivo de este sprint?"
              rows={3}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Capacidad (horas)</Label>
            <Input
              type="number"
              {...register('capacidad_horas')}
              min={0}
              step={0.5}
              placeholder="Horas disponibles del equipo"
              className="mt-1"
            />
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Guardando...' : sprint ? 'Guardar cambios' : 'Crear sprint'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
